import type { FilterObject } from '@moment/trading-types/filter-expression'
import type { HTMLInputTypeAttribute } from 'react'
import type {
  BannerStats,
  SleeveStats,
} from '@/portfolio/proposal-builder/data/helpers/banner-stats'

import {
  Currency,
  MoodysRating,
  SPRating,
} from '@moment/trading-types/reference-data'

import {
  getRatingDisplay,
  type useGetRatingsAccess,
} from '@/hooks/use-rating-columns'
import { globalLogger } from '@/lib/logger'
import { formatMoneyNoCurrency } from '@/portfolio/proposal-builder/formatting-constants'
import { CURRENCY_LABELS } from '@/shared/constants/reference-data-labels'
import { ValueUnit } from '@/types/enums/value-unit'
import { convertNumberToMoodysRating } from '@/utils/moodysRatings'
import { formatBps, formatYield } from '@/utils/presentation/bonds'
import {
  formatMoney,
  formatNumber,
  formatPercentage,
  PLACEHOLDER,
} from '@/utils/presentation/formatting'
import {
  DEFAULT_TRANSFORMS,
  referenceDataTransformationConfig,
} from '@/utils/presentation/instrument-data-transforms'
import { convertNumberToSpRating } from '@/utils/spRatings'
import { getUSARegionOptions } from '@/utils/usa-region-options'

import { DEFAULT_MODEL_NAME, DEFAULT_PROPOSAL_NAME } from './constants'
import { formatYears, PROPOSAL_FORMATTING } from './formatting-constants'
import {
  type AssetClass,
  type BondFeaturesRiskFactorConfig,
  type CashflowTargetRiskFactorConfig,
  ConfigKey,
  type ConventionalRiskFactorConfig,
  DEFAULT_INVESTMENT_AMOUNT,
  Field,
  FieldConfigType,
  type FilterListRiskFactorConfig,
  type LadderPolicyRiskFactorConfig,
  NONE_SECONDARY,
  type ProposalBuilderMode,
  type ResolvedRiskFactorComparison,
  type RiskFactorConfig,
  RiskFactorConfigType,
  StatId,
} from './types'

export const ALL_ASSET_CLASSES: AssetClass[] = [
  'agency',
  'corporate',
  'government',
  'money_market',
  'municipal',
  'securitized',
  'treasury',
  'cd',
]

// Default bucket boundaries for quantitative fields [0, 1, 2, ..., 30]
const DEFAULT_QUANTITATIVE_BUCKETS = Array.from({ length: 31 }, (_, i) => i)

// Bucket boundaries for days-to-maturity-from-next-call: 20 buckets of 30 days [0, 30, ..., 600]
const DAYS_TO_MATURITY_FROM_NEXT_CALL_BUCKETS = Array.from(
  { length: 21 },
  (_, i) => i * 30,
)

const DEFAULT_SP_RATING_BUCKETS = Array.from(
  { length: Object.values(SPRating).length },
  (_, i) => i,
)
const DEFAULT_MOODYS_RATING_BUCKETS = Array.from(
  { length: Object.values(MoodysRating).length },
  (_, i) => i,
)

// Default bucket boundaries for spread fields [-100, -75, -50, ..., 500]
const DEFAULT_SPREAD_BPS_BUCKETS = Array.from(
  { length: 25 },
  (_, i) => -100 + i * 25,
)

function refDataDisplayName(field: Field): string {
  const config =
    referenceDataTransformationConfig[
      field as keyof typeof referenceDataTransformationConfig
    ]
  if (!config) {
    globalLogger.warn('No display name found for field', { field })
    return DEFAULT_TRANSFORMS.keyTransformation(field)
  }
  return config.keyTransformation(field)
}

function refDataFormatValue(field: Field): (value: unknown) => string {
  const config =
    referenceDataTransformationConfig[
      field as keyof typeof referenceDataTransformationConfig
    ]
  if (!config) {
    globalLogger.warn('No format value found for field', { field })
    return DEFAULT_TRANSFORMS.valueTransformation as (value: unknown) => string
  }
  return config.valueTransformation as (value: unknown) => string
}

export const RISK_FACTOR_CONFIGS: Record<string, RiskFactorConfig> = {
  // Visible by default
  // Asset class
  [ConfigKey.Type]: {
    configKey: ConfigKey.Type,
    displayName: refDataDisplayName(Field.Type),
    type: 'conventional',
    fields: [
      {
        field: Field.Type,
        displayName: refDataDisplayName(Field.Type),
        formatValue: refDataFormatValue(Field.Type),
        config: {
          type: FieldConfigType.Categorical,
          entityLabel: { singular: 'asset class', plural: 'asset classes' },
        },
      },
    ],
    relevantAssetClasses: ALL_ASSET_CLASSES,
  } as ConventionalRiskFactorConfig,

  // Lowest rating
  lowest_rating: {
    configKey: ConfigKey.LowestRating,
    displayName: refDataDisplayName(Field.LowestRating),
    type: 'conventional',
    fields: [
      {
        field: Field.LowestRating,
        displayName: refDataDisplayName(Field.LowestRating),
        formatValue: refDataFormatValue(Field.LowestRating),
        config: {
          type: FieldConfigType.Quantitative,
          buckets: DEFAULT_SP_RATING_BUCKETS,
          valueUnit: ValueUnit.Rating,
          tickSize: 1,
        },
      },
    ],
    relevantAssetClasses: ALL_ASSET_CLASSES,
  } as ConventionalRiskFactorConfig,

  // Coupon type
  coupon_type: {
    configKey: ConfigKey.CouponType,
    displayName: refDataDisplayName(Field.CouponType),
    type: 'conventional',
    fields: [
      {
        field: Field.CouponType,
        displayName: refDataDisplayName(Field.CouponType),
        formatValue: refDataFormatValue(Field.CouponType),
        config: {
          type: FieldConfigType.Categorical,
          entityLabel: { singular: 'coupon type', plural: 'coupon types' },
        },
      },
    ],
  } as ConventionalRiskFactorConfig,

  // Coupon
  coupon_value: {
    configKey: ConfigKey.CouponValue,
    displayName: refDataDisplayName(Field.Coupon),
    type: 'conventional',
    fields: [
      {
        field: Field.Coupon,
        displayName: refDataDisplayName(Field.Coupon),
        formatValue: refDataFormatValue(Field.Coupon),
        config: {
          type: FieldConfigType.Quantitative,
          buckets: Array.from({ length: 11 }, (_, i) => i),
          valueUnit: ValueUnit.Percentage,
          tickSize: 0.01,
        },
      },
    ],
  } as ConventionalRiskFactorConfig,

  // Maturity year
  maturity_year: {
    configKey: ConfigKey.MaturityYear,
    displayName: refDataDisplayName(Field.MaturityYear),
    type: 'conventional',
    fields: [
      {
        field: Field.MaturityYear,
        displayName: refDataDisplayName(Field.MaturityYear),
        formatValue: refDataFormatValue(Field.MaturityYear),
        config: {
          type: FieldConfigType.Quantitative,
          buckets: Array.from(
            { length: 32 },
            (_, i) => new Date().getFullYear() + i,
          ),
          tickSize: 1,
          isDiscrete: true,
        },
      },
    ],
  } as ConventionalRiskFactorConfig,

  // Workout year
  workout_year: {
    configKey: ConfigKey.WorkoutYear,
    displayName: refDataDisplayName(Field.WorkoutYear),
    type: 'conventional',
    fields: [
      {
        field: Field.WorkoutYear,
        displayName: refDataDisplayName(Field.WorkoutYear),
        formatValue: refDataFormatValue(Field.WorkoutYear),
        config: {
          type: FieldConfigType.Quantitative,
          buckets: Array.from(
            { length: 32 },
            (_, i) => new Date().getFullYear() + i,
          ),
          tickSize: 1,
          isDiscrete: true,
        },
      },
    ],
  } as ConventionalRiskFactorConfig,

  // 6. Duration
  duration: {
    configKey: ConfigKey.Duration,
    displayName: refDataDisplayName(Field.Duration),
    type: 'conventional',
    fields: [
      {
        field: Field.Duration,
        displayName: refDataDisplayName(Field.Duration),
        formatValue: refDataFormatValue(Field.Duration),
        config: {
          type: FieldConfigType.Quantitative,
          buckets: DEFAULT_QUANTITATIVE_BUCKETS,
          tickSize: 0.01,
        },
      },
    ],
  } as ConventionalRiskFactorConfig,

  // Years to maturity
  maturity_in_years: {
    configKey: ConfigKey.MaturityInYears,
    displayName: refDataDisplayName(Field.MaturityInYears),
    type: 'conventional',
    fields: [
      {
        field: Field.MaturityInYears,
        displayName: refDataDisplayName(Field.MaturityInYears),
        formatValue: refDataFormatValue(Field.MaturityInYears),
        config: {
          type: FieldConfigType.Quantitative,
          buckets: DEFAULT_QUANTITATIVE_BUCKETS,
          valueUnit: ValueUnit.Years,
          tickSize: 0.01,
        },
      },
    ],
  } as ConventionalRiskFactorConfig,

  // Years to workout
  workout_in_years: {
    configKey: ConfigKey.WorkoutInYears,
    displayName: refDataDisplayName(Field.WorkoutInYears),
    type: 'conventional',
    fields: [
      {
        field: Field.WorkoutInYears,
        displayName: refDataDisplayName(Field.WorkoutInYears),
        formatValue: refDataFormatValue(Field.WorkoutInYears),
        config: {
          type: FieldConfigType.Quantitative,
          buckets: DEFAULT_QUANTITATIVE_BUCKETS,
          valueUnit: ValueUnit.Years,
          tickSize: 0.01,
        },
      },
    ],
  } as ConventionalRiskFactorConfig,

  // Years to next call
  next_call_in_years: {
    configKey: ConfigKey.NextCallInYears,
    displayName: refDataDisplayName(Field.NextCallInYears),
    type: 'conventional',
    fields: [
      {
        field: Field.NextCallInYears,
        displayName: refDataDisplayName(Field.NextCallInYears),
        formatValue: refDataFormatValue(Field.NextCallInYears),
        config: {
          type: FieldConfigType.Quantitative,
          buckets: DEFAULT_QUANTITATIVE_BUCKETS,
          valueUnit: ValueUnit.Years,
          tickSize: 0.01,
        },
      },
    ],
    relevantAssetClasses: ALL_ASSET_CLASSES,
  } as ConventionalRiskFactorConfig,

  // Days to maturity from next call
  days_to_maturity_from_next_call: {
    configKey: ConfigKey.DaysToMaturityFromNextCall,
    displayName: refDataDisplayName(Field.DaysToMaturityFromNextCall),
    type: 'conventional',
    fields: [
      {
        field: Field.DaysToMaturityFromNextCall,
        displayName: refDataDisplayName(Field.DaysToMaturityFromNextCall),
        formatValue: refDataFormatValue(Field.DaysToMaturityFromNextCall),
        config: {
          type: FieldConfigType.Quantitative,
          buckets: DAYS_TO_MATURITY_FROM_NEXT_CALL_BUCKETS,
          valueUnit: ValueUnit.Days,
          tickSize: 1,
        },
      },
    ],
    relevantAssetClasses: ALL_ASSET_CLASSES,
  } as ConventionalRiskFactorConfig,

  // Outstanding amount
  outstanding_amount: {
    configKey: ConfigKey.OutstandingAmount,
    displayName: refDataDisplayName(Field.OutstandingAmount),
    type: 'conventional',
    fields: [
      {
        field: Field.OutstandingAmount,
        displayName: refDataDisplayName(Field.OutstandingAmount),
        formatValue: (value: unknown) =>
          `${formatMoneyNoCurrency(Number(value), { numDigits: 0 })}M`,
        config: {
          type: FieldConfigType.Quantitative,
          buckets: Array.from({ length: 51 }, (_, i) => i * 100_000_000),
          valueUnit: ValueUnit.MillionsOfDollars,
          tickSize: 1,
        },
      },
    ],
    relevantAssetClasses: ALL_ASSET_CLASSES,
  } as ConventionalRiskFactorConfig,

  // Seniority type
  seniority_type: {
    configKey: ConfigKey.SeniorityType,
    displayName: refDataDisplayName(Field.SeniorityType),
    type: 'conventional',
    fields: [
      {
        field: Field.SeniorityType,
        displayName: refDataDisplayName(Field.SeniorityType),
        formatValue: refDataFormatValue(Field.SeniorityType),
        config: {
          type: FieldConfigType.Categorical,
          entityLabel: {
            singular: 'seniority type',
            plural: 'seniority types',
          },
        },
      },
    ],
    relevantAssetClasses: ALL_ASSET_CLASSES,
  } as ConventionalRiskFactorConfig,

  // Seniority
  seniority: {
    configKey: ConfigKey.Seniority,
    displayName: refDataDisplayName(Field.Seniority),
    type: 'conventional',
    fields: [
      {
        field: Field.Seniority,
        displayName: refDataDisplayName(Field.Seniority),
        formatValue: refDataFormatValue(Field.Seniority),
        config: {
          type: FieldConfigType.Categorical,
          entityLabel: { singular: 'seniority', plural: 'seniorities' },
        },
      },
    ],
    relevantAssetClasses: ALL_ASSET_CLASSES,
  } as ConventionalRiskFactorConfig,

  // Issuer
  issuer: {
    configKey: ConfigKey.Issuer,
    displayName: refDataDisplayName(Field.Issuer),
    type: 'conventional',
    fields: [
      {
        field: Field.Issuer,
        displayName: refDataDisplayName(Field.Issuer),
        formatValue: refDataFormatValue(Field.Issuer),
        config: {
          type: FieldConfigType.Categorical,
          entityLabel: { singular: 'issuer', plural: 'issuers' },
        },
      },
    ],
    relevantAssetClasses: ALL_ASSET_CLASSES,
  } as ConventionalRiskFactorConfig,

  // Ultimate parent
  ultimate_parent: {
    configKey: ConfigKey.UltimateParent,
    displayName: refDataDisplayName(Field.UltimateParent),
    type: 'conventional',
    fields: [
      {
        field: Field.UltimateParent,
        displayName: refDataDisplayName(Field.UltimateParent),
        formatValue: refDataFormatValue(Field.UltimateParent),
        config: {
          type: FieldConfigType.Categorical,
          entityLabel: {
            singular: 'ultimate parent',
            plural: 'ultimate parents',
          },
        },
      },
    ],
    relevantAssetClasses: ALL_ASSET_CLASSES,
  } as ConventionalRiskFactorConfig,

  // Ultimate borrower
  ultimate_borrower: {
    configKey: ConfigKey.UltimateBorrower,
    displayName: refDataDisplayName(Field.UltimateBorrower),
    type: 'conventional',
    fields: [
      {
        field: Field.UltimateBorrower,
        displayName: refDataDisplayName(Field.UltimateBorrower),
        formatValue: refDataFormatValue(Field.UltimateBorrower),
        config: {
          type: FieldConfigType.Categorical,
          entityLabel: {
            singular: 'ultimate borrower',
            plural: 'ultimate borrowers',
          },
        },
      },
    ],
    relevantAssetClasses: ALL_ASSET_CLASSES,
  } as ConventionalRiskFactorConfig,

  // Municipal federal tax status
  muni_tax_status: {
    configKey: ConfigKey.MuniTaxStatus,
    displayName: refDataDisplayName(Field.MuniTaxStatus),
    type: 'conventional',
    fields: [
      {
        field: Field.MuniTaxStatus,
        displayName: refDataDisplayName(Field.MuniTaxStatus),
        formatValue: refDataFormatValue(Field.MuniTaxStatus),
        config: {
          type: FieldConfigType.Categorical,
          entityLabel: {
            singular: 'muni federal tax status',
            plural: 'muni federal tax statuses',
          },
        },
      },
    ],
    relevantAssetClasses: ['municipal'],
  } as ConventionalRiskFactorConfig,

  // Municipal state
  municipal_state: {
    configKey: ConfigKey.MunicipalState,
    displayName: refDataDisplayName(Field.MunicipalState),
    type: 'conventional',
    fields: [
      {
        field: Field.MunicipalState,
        displayName: refDataDisplayName(Field.MunicipalState),
        formatValue: refDataFormatValue(Field.MunicipalState),
        config: {
          type: FieldConfigType.Categorical,
          entityLabel: { singular: 'state', plural: 'states' },
        },
      },
    ],
    relevantAssetClasses: ['municipal'],
  } as ConventionalRiskFactorConfig,

  // Municipal obligor state
  muni_obligor_state: {
    configKey: ConfigKey.MuniObligorState,
    displayName: refDataDisplayName(Field.MuniObligorState),
    type: 'conventional',
    fields: [
      {
        field: Field.MuniObligorState,
        displayName: refDataDisplayName(Field.MuniObligorState),
        formatValue: refDataFormatValue(Field.MuniObligorState),
        config: {
          type: FieldConfigType.Categorical,
          entityLabel: { singular: 'state', plural: 'states' },
        },
      },
    ],
    relevantAssetClasses: ['municipal'],
  } as ConventionalRiskFactorConfig,

  // Municipal type
  municipal_type: {
    configKey: ConfigKey.MunicipalType,
    displayName: refDataDisplayName(Field.MunicipalType),
    type: 'conventional',
    fields: [
      {
        field: Field.MunicipalType,
        displayName: refDataDisplayName(Field.MunicipalType),
        formatValue: refDataFormatValue(Field.MunicipalType),
        config: {
          type: FieldConfigType.Categorical,
          entityLabel: {
            singular: 'municipal type',
            plural: 'municipal types',
          },
        },
      },
    ],
    relevantAssetClasses: ['municipal'],
  } as ConventionalRiskFactorConfig,

  // Use of proceeds
  use_of_proceeds: {
    configKey: ConfigKey.UseOfProceeds,
    displayName: refDataDisplayName(Field.UseOfProceeds),
    type: 'conventional',
    fields: [
      {
        field: Field.UseOfProceeds,
        displayName: refDataDisplayName(Field.UseOfProceeds),
        formatValue: refDataFormatValue(Field.UseOfProceeds),
        config: {
          type: FieldConfigType.Categorical,
          entityLabel: {
            singular: 'use of proceeds',
            plural: 'uses of proceeds',
          },
        },
      },
    ],
    relevantAssetClasses: ['municipal'],
  } as ConventionalRiskFactorConfig,

  // FDIC code
  [ConfigKey.FDICCode]: {
    configKey: ConfigKey.FDICCode,
    displayName: refDataDisplayName(Field.FDICCode),
    type: 'conventional',
    fields: [
      {
        field: Field.FDICCode,
        displayName: refDataDisplayName(Field.FDICCode),
        formatValue: refDataFormatValue(Field.FDICCode),
        config: {
          type: FieldConfigType.Categorical,
          entityLabel: {
            singular: 'FDIC code',
            plural: 'FDIC codes',
          },
        },
      },
    ],
    relevantAssetClasses: ['cd'],
  } as ConventionalRiskFactorConfig,

  // Treasury attributes (treasury subtype)
  treasury_subtype: {
    configKey: ConfigKey.TreasurySubtype,
    displayName: refDataDisplayName(Field.TreasurySubtype),
    type: 'conventional',
    fields: [
      {
        field: Field.TreasurySubtype,
        displayName: refDataDisplayName(Field.TreasurySubtype),
        formatValue: refDataFormatValue(Field.TreasurySubtype),
        config: {
          type: FieldConfigType.Categorical,
          entityLabel: {
            singular: 'treasury type',
            plural: 'treasury types',
          },
        },
      },
    ],
    relevantAssetClasses: ['treasury'],
  } as ConventionalRiskFactorConfig,

  // Price
  price: {
    configKey: ConfigKey.Price,
    displayName: 'Price',
    type: 'conventional',
    fields: [
      {
        field: Field.Price,
        displayName: refDataDisplayName(Field.Price),
        formatValue: (value: unknown) =>
          `${formatMoneyNoCurrency(Number(value), { numDigits: PROPOSAL_FORMATTING.PRICE_DECIMALS })}`,
        config: {
          type: FieldConfigType.Quantitative,
          buckets: Array.from({ length: 101 }, (_, i) => 50 + i),
          valueUnit: ValueUnit.Dollars,
          tickSize: 0.01,
        },
      },
    ],
  } as ConventionalRiskFactorConfig,

  // Yield to worst
  yield_to_worst: {
    configKey: ConfigKey.YieldToWorst,
    displayName: 'Yield to worst',
    type: 'conventional',
    fields: [
      {
        field: Field.YieldToWorst,
        displayName: refDataDisplayName(Field.YieldToWorst),
        formatValue: (value: unknown) =>
          `${formatPercentage(value as number | null, { numDigits: PROPOSAL_FORMATTING.YTW_DECIMALS })}`,
        config: {
          type: FieldConfigType.Quantitative,
          buckets: Array.from({ length: 41 }, (_, i) => i * 0.25),
          valueUnit: ValueUnit.Percentage,
          tickSize: 0.01,
        },
      },
      {
        field: Field.BidYieldToWorst,
        displayName: refDataDisplayName(Field.BidYieldToWorst),
        formatValue: (value: unknown) =>
          `${formatPercentage(value as number | null, { numDigits: PROPOSAL_FORMATTING.YTW_DECIMALS })}`,
        config: {
          type: FieldConfigType.Quantitative,
          buckets: Array.from({ length: 41 }, (_, i) => i * 0.25),
          valueUnit: ValueUnit.Percentage,
          tickSize: 0.01,
        },
      },
      {
        field: Field.AskYieldToWorst,
        displayName: refDataDisplayName(Field.AskYieldToWorst),
        formatValue: (value: unknown) =>
          `${formatPercentage(value as number | null, { numDigits: PROPOSAL_FORMATTING.YTW_DECIMALS })}`,
        config: {
          type: FieldConfigType.Quantitative,
          buckets: Array.from({ length: 41 }, (_, i) => i * 0.25),
          valueUnit: ValueUnit.Percentage,
          tickSize: 0.01,
        },
      },
    ],
  } as ConventionalRiskFactorConfig,

  // Yield to maturity
  yield_to_maturity: {
    configKey: ConfigKey.YieldToMaturity,
    displayName: 'Yield to maturity',
    type: 'conventional',
    fields: [
      {
        field: Field.YieldToMaturity,
        displayName: refDataDisplayName(Field.YieldToMaturity),
        formatValue: (value: unknown) =>
          `${formatPercentage(value as number | null, { numDigits: PROPOSAL_FORMATTING.YTW_DECIMALS })}`,
        config: {
          type: FieldConfigType.Quantitative,
          buckets: Array.from({ length: 41 }, (_, i) => i * 0.25),
          valueUnit: ValueUnit.Percentage,
          tickSize: 0.01,
        },
      },
      {
        field: Field.BidYieldToMaturity,
        displayName: refDataDisplayName(Field.BidYieldToMaturity),
        formatValue: (value: unknown) =>
          `${formatPercentage(value as number | null, { numDigits: PROPOSAL_FORMATTING.YTW_DECIMALS })}`,
        config: {
          type: FieldConfigType.Quantitative,
          buckets: Array.from({ length: 41 }, (_, i) => i * 0.25),
          valueUnit: ValueUnit.Percentage,
          tickSize: 0.01,
        },
      },
      {
        field: Field.AskYieldToMaturity,
        displayName: refDataDisplayName(Field.AskYieldToMaturity),
        formatValue: (value: unknown) =>
          `${formatPercentage(value as number | null, { numDigits: PROPOSAL_FORMATTING.YTW_DECIMALS })}`,
        config: {
          type: FieldConfigType.Quantitative,
          buckets: Array.from({ length: 41 }, (_, i) => i * 0.25),
          valueUnit: ValueUnit.Percentage,
          tickSize: 0.01,
        },
      },
    ],
  } as ConventionalRiskFactorConfig,

  // Yield to call
  yield_to_call: {
    configKey: ConfigKey.YieldToCall,
    displayName: 'Yield to call',
    type: 'conventional',
    fields: [
      {
        field: Field.MidYieldToCall,
        displayName: refDataDisplayName(Field.MidYieldToCall),
        formatValue: (value: unknown) =>
          `${formatPercentage(value as number | null, { numDigits: PROPOSAL_FORMATTING.YTW_DECIMALS })}`,
        config: {
          type: FieldConfigType.Quantitative,
          buckets: Array.from({ length: 41 }, (_, i) => i * 0.25),
          valueUnit: ValueUnit.Percentage,
          tickSize: 0.01,
        },
      },
      {
        field: Field.BidYieldToCall,
        displayName: refDataDisplayName(Field.BidYieldToCall),
        formatValue: (value: unknown) =>
          `${formatPercentage(value as number | null, { numDigits: PROPOSAL_FORMATTING.YTW_DECIMALS })}`,
        config: {
          type: FieldConfigType.Quantitative,
          buckets: Array.from({ length: 41 }, (_, i) => i * 0.25),
          valueUnit: ValueUnit.Percentage,
          tickSize: 0.01,
        },
      },
      {
        field: Field.AskYieldToCall,
        displayName: refDataDisplayName(Field.AskYieldToCall),
        formatValue: (value: unknown) =>
          `${formatPercentage(value as number | null, { numDigits: PROPOSAL_FORMATTING.YTW_DECIMALS })}`,
        config: {
          type: FieldConfigType.Quantitative,
          buckets: Array.from({ length: 41 }, (_, i) => i * 0.25),
          valueUnit: ValueUnit.Percentage,
          tickSize: 0.01,
        },
      },
    ],
  } as ConventionalRiskFactorConfig,

  // Trading restrictions
  trading_restrictions_type: {
    configKey: ConfigKey.TradingRestrictionsType,
    displayName: refDataDisplayName(Field.TradingRestrictionsType),
    type: 'conventional',
    fields: [
      {
        field: Field.TradingRestrictionsType,
        displayName: refDataDisplayName(Field.TradingRestrictionsType),
        formatValue: refDataFormatValue(Field.TradingRestrictionsType),
        config: {
          type: FieldConfigType.Categorical,
          entityLabel: {
            singular: 'trading restriction',
            plural: 'trading restrictions',
          },
        },
      },
    ],
    relevantAssetClasses: ALL_ASSET_CLASSES,
  } as ConventionalRiskFactorConfig,

  // Features
  bond_features: {
    configKey: ConfigKey.BondFeatures,
    displayName: 'Features',
    type: 'bond_features',
    fields: [
      {
        field: Field.IsAmtApplicable,
        displayName: refDataDisplayName(Field.IsAmtApplicable),
        relevantAssetClasses: ['municipal'],
      },
      {
        field: Field.BankQualified,
        displayName: refDataDisplayName(Field.BankQualified),
        relevantAssetClasses: ['municipal'],
      },
      {
        field: Field.BondWarrant,
        displayName: refDataDisplayName(Field.BondWarrant),
        relevantAssetClasses: ['corporate'],
      },
      {
        field: Field.Callable,
        displayName: refDataDisplayName(Field.Callable),
        relevantAssetClasses: [
          'agency',
          'corporate',
          'municipal',
          'cd',
          'government',
        ],
      },
      {
        field: Field.Convertible,
        displayName: refDataDisplayName(Field.Convertible),
        relevantAssetClasses: ['corporate'],
      },
      {
        field: Field.ExtraordinaryCall,
        displayName: refDataDisplayName(Field.ExtraordinaryCall),
        relevantAssetClasses: ['corporate', 'municipal'],
      },
      {
        field: Field.FDICInsured,
        displayName: refDataDisplayName(Field.FDICInsured),
        relevantAssetClasses: ['cd'],
      },
      {
        field: Field.Insured,
        displayName: refDataDisplayName(Field.Insured),
        relevantAssetClasses: ['corporate', 'municipal', 'cd', 'government'],
      },
      {
        field: Field.MakeWholeCallable,
        displayName: refDataDisplayName(Field.MakeWholeCallable),
        relevantAssetClasses: [
          'corporate',
          'government',
          'municipal',
          'agency',
        ],
      },
      {
        field: Field.Perpetual,
        displayName: refDataDisplayName(Field.Perpetual),
        relevantAssetClasses: ['corporate', 'government'],
      },
      {
        field: Field.PrivatePlacement,
        displayName: refDataDisplayName(Field.PrivatePlacement),
        relevantAssetClasses: ['corporate'],
      },
      {
        field: Field.RegS,
        displayName: refDataDisplayName(Field.RegS),
        relevantAssetClasses: ['corporate'],
      },
      {
        field: Field.MunicipalTaxableFederal,
        displayName: refDataDisplayName(Field.MunicipalTaxableFederal),
        relevantAssetClasses: ['municipal'],
      },
      {
        field: Field.PreRefunded,
        displayName: refDataDisplayName(Field.PreRefunded),
        relevantAssetClasses: ['municipal'],
      },
      {
        field: Field.SinkingFund,
        displayName: refDataDisplayName(Field.SinkingFund),
        relevantAssetClasses: ['corporate', 'agency', 'municipal'],
      },
      {
        field: Field.IsStateTaxApplicable,
        displayName: refDataDisplayName(Field.IsStateTaxApplicable),
        relevantAssetClasses: ['municipal'],
      },
      {
        field: Field.TraceEligible,
        displayName: refDataDisplayName(Field.TraceEligible),
        relevantAssetClasses: ['corporate', 'government', 'agency'],
      },
      {
        field: Field.VrdoIndicator,
        displayName: refDataDisplayName(Field.VrdoIndicator),
        relevantAssetClasses: ['municipal'],
      },
      {
        field: Field.CocoIndicator,
        displayName: refDataDisplayName(Field.CocoIndicator),
        relevantAssetClasses: ['corporate'],
      },
      {
        field: Field.QcbIndicator,
        displayName: refDataDisplayName(Field.QcbIndicator),
        relevantAssetClasses: ['corporate'],
      },
      {
        field: Field.SukukIndicator,
        displayName: refDataDisplayName(Field.SukukIndicator),
        relevantAssetClasses: ALL_ASSET_CLASSES,
      },
      {
        field: Field.SurvivorOption,
        displayName: refDataDisplayName(Field.SurvivorOption),
        relevantAssetClasses: ALL_ASSET_CLASSES,
      },
    ],
  } as BondFeaturesRiskFactorConfig,

  // Cashflow targets
  cashflow_target: {
    configKey: ConfigKey.CashflowTarget,
    displayName: 'Cash flow',
    type: RiskFactorConfigType.CashflowTarget,
  } as CashflowTargetRiskFactorConfig,

  // Ladder policy
  ladder_policy: {
    configKey: ConfigKey.LadderPolicy,
    displayName: 'Ladder policy',
    type: RiskFactorConfigType.LadderPolicy,
  } as LadderPolicyRiskFactorConfig,

  // Hidden by default
  // ICE sector (L1 / L2 / L3 / L4)
  ice_sector_1: {
    configKey: ConfigKey.IceSector1,
    displayName: refDataDisplayName(Field.IceSector1),
    type: 'conventional',
    fields: [
      {
        field: Field.IceSector1,
        displayName: refDataDisplayName(Field.IceSector1),
        formatValue: refDataFormatValue(Field.IceSector1),
        config: {
          type: FieldConfigType.Categorical,
          entityLabel: { singular: 'class', plural: 'classes' },
        },
      },
    ],
    relevantAssetClasses: ALL_ASSET_CLASSES,
  } as ConventionalRiskFactorConfig,

  ice_sector_2: {
    configKey: ConfigKey.IceSector2,
    displayName: refDataDisplayName(Field.IceSector2),
    type: 'conventional',
    fields: [
      {
        field: Field.IceSector2,
        displayName: refDataDisplayName(Field.IceSector2),
        formatValue: refDataFormatValue(Field.IceSector2),
        config: {
          type: FieldConfigType.Categorical,
          entityLabel: { singular: 'group', plural: 'groups' },
        },
      },
    ],
    relevantAssetClasses: ALL_ASSET_CLASSES,
  } as ConventionalRiskFactorConfig,

  ice_sector_3: {
    configKey: ConfigKey.IceSector3,
    displayName: refDataDisplayName(Field.IceSector3),
    type: 'conventional',
    fields: [
      {
        field: Field.IceSector3,
        displayName: refDataDisplayName(Field.IceSector3),
        formatValue: refDataFormatValue(Field.IceSector3),
        config: {
          type: FieldConfigType.Categorical,
          entityLabel: { singular: 'category', plural: 'categories' },
        },
      },
    ],
    relevantAssetClasses: ALL_ASSET_CLASSES,
  } as ConventionalRiskFactorConfig,

  ice_sector_4: {
    configKey: ConfigKey.IceSector4,
    displayName: 'ICE sector sub-category (L4)',
    type: 'conventional',
    fields: [
      {
        field: Field.IceSector4,
        displayName: refDataDisplayName(Field.IceSector4),
        formatValue: refDataFormatValue(Field.IceSector4),
        config: {
          type: FieldConfigType.Categorical,
          entityLabel: { singular: 'sub-category', plural: 'sub-categories' },
        },
      },
    ],
    relevantAssetClasses: ALL_ASSET_CLASSES,
  } as ConventionalRiskFactorConfig,

  // 2. Filter lists
  filter_lists: {
    configKey: ConfigKey.FilterLists,
    displayName: 'Filter lists',
    type: 'filter_list',
  } as FilterListRiskFactorConfig,

  // Country of domicile
  country_domicile: {
    configKey: ConfigKey.CountryDomicile,
    displayName: refDataDisplayName(Field.CountryDomicile),
    type: 'conventional',
    fields: [
      {
        field: Field.CountryDomicile,
        displayName: refDataDisplayName(Field.CountryDomicile),
        formatValue: refDataFormatValue(Field.CountryDomicile),
        config: {
          type: FieldConfigType.Categorical,
          entityLabel: { singular: 'country', plural: 'countries' },
        },
      },
    ],
    relevantAssetClasses: ALL_ASSET_CLASSES,
  } as ConventionalRiskFactorConfig,

  // Country of issue
  country_issue: {
    configKey: ConfigKey.CountryIssue,
    displayName: refDataDisplayName(Field.CountryIssue),
    type: 'conventional',
    fields: [
      {
        field: Field.CountryIssue,
        displayName: refDataDisplayName(Field.CountryIssue),
        formatValue: refDataFormatValue(Field.CountryIssue),
        config: {
          type: FieldConfigType.Categorical,
          entityLabel: { singular: 'country', plural: 'countries' },
        },
      },
    ],
    relevantAssetClasses: ALL_ASSET_CLASSES,
  } as ConventionalRiskFactorConfig,

  // Country of risk
  country_of_risk: {
    configKey: ConfigKey.CountryOfRisk,
    displayName: refDataDisplayName(Field.CountryOfRisk),
    type: 'conventional',
    fields: [
      {
        field: Field.CountryOfRisk,
        displayName: refDataDisplayName(Field.CountryOfRisk),
        formatValue: refDataFormatValue(Field.CountryOfRisk),
        config: {
          type: FieldConfigType.Categorical,
          entityLabel: {
            singular: 'country of risk',
            plural: 'countries of risk',
          },
        },
      },
    ],
    relevantAssetClasses: ALL_ASSET_CLASSES,
  } as ConventionalRiskFactorConfig,

  // Emerging status
  emerging_status: {
    configKey: ConfigKey.EmergingStatus,
    displayName: refDataDisplayName(Field.EmergingStatus),
    type: 'conventional',
    fields: [
      {
        field: Field.EmergingStatus,
        displayName: 'Emerging status',
        formatValue: (value: unknown) =>
          DEFAULT_TRANSFORMS.valueTransformation(value),
        config: {
          type: FieldConfigType.Categorical,
          entityLabel: {
            singular: 'emerging status',
            plural: 'emerging statuses',
          },
        },
      },
    ],
    relevantAssetClasses: ALL_ASSET_CLASSES,
  } as ConventionalRiskFactorConfig,

  // Credit quality class
  credit_quality_class: {
    configKey: ConfigKey.CreditQualityClass,
    displayName: 'Credit quality',
    type: 'conventional',
    fields: [
      {
        field: Field.CreditQualityClass,
        displayName: 'Credit quality',
        formatValue: refDataFormatValue(Field.CreditQualityClass),
        config: {
          type: FieldConfigType.Categorical,
          entityLabel: {
            singular: 'credit quality class',
            plural: 'credit quality classes',
          },
        },
      },
    ],
    relevantAssetClasses: ALL_ASSET_CLASSES,
  } as ConventionalRiskFactorConfig,

  // Impact bond type
  impact_bond_type: {
    configKey: ConfigKey.ImpactBondType,
    displayName: 'Impact bond type',
    type: 'conventional',
    fields: [
      {
        field: Field.ImpactBondType,
        displayName: 'Impact bond type',
        formatValue: (value: unknown) =>
          DEFAULT_TRANSFORMS.valueTransformation(value),
        config: {
          type: FieldConfigType.Categorical,
          entityLabel: {
            singular: 'impact bond type',
            plural: 'impact bond types',
          },
        },
      },
    ],
    relevantAssetClasses: ALL_ASSET_CLASSES,
  } as ConventionalRiskFactorConfig,

  // Call type
  call_type: {
    configKey: ConfigKey.CallType,
    displayName: refDataDisplayName(Field.CallType),
    type: 'conventional',
    fields: [
      {
        field: Field.CallType,
        displayName: refDataDisplayName(Field.CallType),
        formatValue: refDataFormatValue(Field.CallType),
        config: {
          type: FieldConfigType.Categorical,
          entityLabel: { singular: 'call type', plural: 'call types' },
        },
      },
    ],
    relevantAssetClasses: ALL_ASSET_CLASSES,
  } as ConventionalRiskFactorConfig,

  // Call frequency
  call_frequency: {
    configKey: ConfigKey.CallFrequency,
    displayName: refDataDisplayName(Field.CallFrequency),
    type: 'conventional',
    fields: [
      {
        field: Field.CallFrequency,
        displayName: refDataDisplayName(Field.CallFrequency),
        formatValue: refDataFormatValue(Field.CallFrequency),
        config: {
          type: FieldConfigType.Categorical,
          entityLabel: {
            singular: 'call frequency',
            plural: 'call frequencies',
          },
        },
      },
    ],
    relevantAssetClasses: ALL_ASSET_CLASSES,
  } as ConventionalRiskFactorConfig,

  // DTS (Duration Times Spread)
  [ConfigKey.DTS]: {
    configKey: ConfigKey.DTS,
    displayName: refDataDisplayName(Field.DTS),
    type: 'conventional',
    fields: [
      {
        field: Field.DTS,
        displayName: refDataDisplayName(Field.DTS),
        formatValue: refDataFormatValue(Field.DTS),
        config: {
          type: FieldConfigType.Quantitative,
          buckets: Array.from({ length: 51 }, (_, i) => -10 + i),
          tickSize: 0.01,
        },
      },
    ],
    relevantAssetClasses: ALL_ASSET_CLASSES,
  } as ConventionalRiskFactorConfig,

  // S&P rating
  sp_rating: {
    configKey: ConfigKey.SpRating,
    displayName: refDataDisplayName(Field.SpRating),
    type: 'conventional',
    fields: [
      {
        field: Field.SpRating,
        displayName: refDataDisplayName(Field.SpRating),
        formatValue: refDataFormatValue(Field.SpRating),
        config: {
          type: FieldConfigType.Quantitative,
          buckets: DEFAULT_SP_RATING_BUCKETS,
          valueUnit: ValueUnit.Rating,
          tickSize: 1,
        },
      },
    ],
    relevantAssetClasses: ALL_ASSET_CLASSES,
  } as ConventionalRiskFactorConfig,

  // Moody's rating
  moodys_rating: {
    configKey: ConfigKey.MoodysRating,
    displayName: refDataDisplayName(Field.MoodysRating),
    type: 'conventional',
    fields: [
      {
        field: Field.MoodysRating,
        displayName: refDataDisplayName(Field.MoodysRating),
        formatValue: refDataFormatValue(Field.MoodysRating),
        config: {
          type: FieldConfigType.Quantitative,
          buckets: DEFAULT_MOODYS_RATING_BUCKETS,
          valueUnit: ValueUnit.Rating,
          tickSize: 1,
        },
      },
    ],
    relevantAssetClasses: ALL_ASSET_CLASSES,
  } as ConventionalRiskFactorConfig,

  // Issue amount
  issue_amount: {
    configKey: ConfigKey.IssueAmount,
    displayName: 'Issue amount',
    type: 'conventional',
    fields: [
      {
        field: Field.IssueAmount,
        displayName: 'Issue amount',
        formatValue: (value: unknown) =>
          `${formatMoneyNoCurrency(Number(value), { numDigits: 0 })}M`,
        config: {
          type: FieldConfigType.Quantitative,
          buckets: Array.from({ length: 51 }, (_, i) => i * 100_000_000),
          valueUnit: ValueUnit.MillionsOfDollars,
          tickSize: 1,
        },
      },
    ],
    relevantAssetClasses: ALL_ASSET_CLASSES,
  } as ConventionalRiskFactorConfig,

  // Coupon frequency
  coupon_frequency: {
    configKey: ConfigKey.CouponFrequency,
    displayName: refDataDisplayName(Field.CouponFrequency),
    type: 'conventional',
    fields: [
      {
        field: Field.CouponFrequency,
        displayName: refDataDisplayName(Field.CouponFrequency),
        formatValue: refDataFormatValue(Field.CouponFrequency),
        config: {
          type: FieldConfigType.Categorical,
          entityLabel: {
            singular: 'coupon frequency',
            plural: 'coupon frequencies',
          },
        },
      },
    ],
  } as ConventionalRiskFactorConfig,

  // GICS sector (L1-L4)
  gics_sector_1: {
    configKey: ConfigKey.GicsSector1,
    displayName: refDataDisplayName(Field.GicsSector1),
    type: 'conventional',
    fields: [
      {
        field: Field.GicsSector1,
        displayName: refDataDisplayName(Field.GicsSector1),
        formatValue: refDataFormatValue(Field.GicsSector1),
        config: {
          type: FieldConfigType.Categorical,
          entityLabel: { singular: 'sector', plural: 'sectors' },
        },
      },
    ],
    relevantAssetClasses: ['corporate', 'cd', 'agency'],
  } as ConventionalRiskFactorConfig,

  gics_sector_2: {
    configKey: ConfigKey.GicsSector2,
    displayName: refDataDisplayName(Field.GicsSector2),
    type: 'conventional',
    fields: [
      {
        field: Field.GicsSector2,
        displayName: refDataDisplayName(Field.GicsSector2),
        formatValue: refDataFormatValue(Field.GicsSector2),
        config: {
          type: FieldConfigType.Categorical,
          entityLabel: {
            singular: 'industry group',
            plural: 'industry groups',
          },
        },
      },
    ],
    relevantAssetClasses: ['corporate', 'cd', 'agency'],
  } as ConventionalRiskFactorConfig,

  gics_sector_3: {
    configKey: ConfigKey.GicsSector3,
    displayName: refDataDisplayName(Field.GicsSector3),
    type: 'conventional',
    fields: [
      {
        field: Field.GicsSector3,
        displayName: refDataDisplayName(Field.GicsSector3),
        formatValue: refDataFormatValue(Field.GicsSector3),
        config: {
          type: FieldConfigType.Categorical,
          entityLabel: { singular: 'industry', plural: 'industries' },
        },
      },
    ],
    relevantAssetClasses: ['corporate', 'cd', 'agency'],
  } as ConventionalRiskFactorConfig,

  gics_sector_4: {
    configKey: ConfigKey.GicsSector4,
    displayName: refDataDisplayName(Field.GicsSector4),
    type: 'conventional',
    fields: [
      {
        field: Field.GicsSector4,
        displayName: refDataDisplayName(Field.GicsSector4),
        formatValue: refDataFormatValue(Field.GicsSector4),
        config: {
          type: FieldConfigType.Categorical,
          entityLabel: { singular: 'sub-industry', plural: 'sub-industries' },
        },
      },
    ],
    relevantAssetClasses: ['corporate', 'cd', 'agency'],
  } as ConventionalRiskFactorConfig,

  // Sector (generic)
  generic_sector: {
    configKey: ConfigKey.GenericSector,
    displayName: 'Sector',
    type: 'conventional',
    fields: [
      {
        field: Field.Sector,
        displayName: refDataDisplayName(Field.Sector),
        formatValue: refDataFormatValue(Field.Sector),
        config: {
          type: FieldConfigType.Categorical,
          entityLabel: { singular: 'sector', plural: 'sectors' },
        },
      },
    ],
    relevantAssetClasses: ALL_ASSET_CLASSES,
  } as ConventionalRiskFactorConfig,

  // Ticker
  ticker: {
    configKey: ConfigKey.Ticker,
    displayName: refDataDisplayName(Field.Ticker),
    type: 'conventional',
    fields: [
      {
        field: Field.Ticker,
        displayName: refDataDisplayName(Field.Ticker),
        formatValue: refDataFormatValue(Field.Ticker),
        config: {
          type: FieldConfigType.Categorical,
          entityLabel: { singular: 'ticker', plural: 'tickers' },
        },
      },
    ],
    relevantAssetClasses: ALL_ASSET_CLASSES,
  } as ConventionalRiskFactorConfig,

  // Municipal sector
  muni_sector: {
    configKey: ConfigKey.MuniSector,
    displayName: refDataDisplayName(Field.MuniSector),
    type: 'conventional',
    fields: [
      {
        field: Field.MuniSector,
        displayName: refDataDisplayName(Field.MuniSector),
        formatValue: refDataFormatValue(Field.MuniSector),
        config: {
          type: FieldConfigType.Categorical,
          entityLabel: { singular: 'sector', plural: 'sectors' },
        },
      },
    ],
    relevantAssetClasses: ['municipal'],
  } as ConventionalRiskFactorConfig,

  // 17. Spread to benchmark
  benchmark_spread: {
    configKey: ConfigKey.BenchmarkSpread,
    displayName: 'Spread to benchmark',
    type: 'conventional',
    fields: [
      {
        field: Field.MidBenchmarkYTWSpreadBPS,
        displayName: refDataDisplayName(Field.MidBenchmarkYTWSpreadBPS),
        formatValue: refDataFormatValue(Field.MidBenchmarkYTWSpreadBPS),
        config: {
          type: FieldConfigType.Quantitative,
          buckets: DEFAULT_SPREAD_BPS_BUCKETS,
          valueUnit: ValueUnit.BasisPoints,
          tickSize: 1,
        },
      },
      {
        field: Field.BidBenchmarkYTWSpreadBPS,
        displayName: refDataDisplayName(Field.BidBenchmarkYTWSpreadBPS),
        formatValue: refDataFormatValue(Field.BidBenchmarkYTWSpreadBPS),
        config: {
          type: FieldConfigType.Quantitative,
          buckets: DEFAULT_SPREAD_BPS_BUCKETS,
          valueUnit: ValueUnit.BasisPoints,
          tickSize: 1,
        },
      },
      {
        field: Field.AskBenchmarkYTWSpreadBPS,
        displayName: refDataDisplayName(Field.AskBenchmarkYTWSpreadBPS),
        formatValue: refDataFormatValue(Field.AskBenchmarkYTWSpreadBPS),
        config: {
          type: FieldConfigType.Quantitative,
          buckets: DEFAULT_SPREAD_BPS_BUCKETS,
          valueUnit: ValueUnit.BasisPoints,
          tickSize: 1,
        },
      },
    ],
  } as ConventionalRiskFactorConfig,

  // 18. G-spread
  g_spread: {
    configKey: ConfigKey.GSpread,
    displayName: 'G-spread',
    type: 'conventional',
    fields: [
      {
        field: Field.MidGSpreadBPS,
        displayName: refDataDisplayName(Field.MidGSpreadBPS),
        formatValue: refDataFormatValue(Field.MidGSpreadBPS),
        config: {
          type: FieldConfigType.Quantitative,
          buckets: DEFAULT_SPREAD_BPS_BUCKETS,
          valueUnit: ValueUnit.BasisPoints,
          tickSize: 1,
        },
      },
      {
        field: Field.BidGSpreadBPS,
        displayName: refDataDisplayName(Field.BidGSpreadBPS),
        formatValue: refDataFormatValue(Field.BidGSpreadBPS),
        config: {
          type: FieldConfigType.Quantitative,
          buckets: DEFAULT_SPREAD_BPS_BUCKETS,
          valueUnit: ValueUnit.BasisPoints,
          tickSize: 1,
        },
      },
      {
        field: Field.AskGSpreadBPS,
        displayName: refDataDisplayName(Field.AskGSpreadBPS),
        formatValue: refDataFormatValue(Field.AskGSpreadBPS),
        config: {
          type: FieldConfigType.Quantitative,
          buckets: DEFAULT_SPREAD_BPS_BUCKETS,
          valueUnit: ValueUnit.BasisPoints,
          tickSize: 1,
        },
      },
    ],
  } as ConventionalRiskFactorConfig,

  // 19. I-spread
  i_spread: {
    configKey: ConfigKey.ISpread,
    displayName: 'I-spread',
    type: 'conventional',
    fields: [
      {
        field: Field.MidISpread,
        displayName: refDataDisplayName(Field.MidISpread),
        formatValue: refDataFormatValue(Field.MidISpread),
        config: {
          type: FieldConfigType.Quantitative,
          buckets: DEFAULT_SPREAD_BPS_BUCKETS,
          valueUnit: ValueUnit.BasisPoints,
          tickSize: 1,
        },
      },
      {
        field: Field.BidISpread,
        displayName: refDataDisplayName(Field.BidISpread),
        formatValue: refDataFormatValue(Field.BidISpread),
        config: {
          type: FieldConfigType.Quantitative,
          buckets: DEFAULT_SPREAD_BPS_BUCKETS,
          valueUnit: ValueUnit.BasisPoints,
          tickSize: 1,
        },
      },
      {
        field: Field.AskISpread,
        displayName: refDataDisplayName(Field.AskISpread),
        formatValue: refDataFormatValue(Field.AskISpread),
        config: {
          type: FieldConfigType.Quantitative,
          buckets: DEFAULT_SPREAD_BPS_BUCKETS,
          valueUnit: ValueUnit.BasisPoints,
          tickSize: 1,
        },
      },
    ],
  } as ConventionalRiskFactorConfig,

  // 20. Z-spread
  z_spread: {
    configKey: ConfigKey.ZSpread,
    displayName: 'Z-spread',
    type: 'conventional',
    fields: [
      {
        field: Field.MidZSpreadBPS,
        displayName: refDataDisplayName(Field.MidZSpreadBPS),
        formatValue: refDataFormatValue(Field.MidZSpreadBPS),
        config: {
          type: FieldConfigType.Quantitative,
          buckets: DEFAULT_SPREAD_BPS_BUCKETS,
          valueUnit: ValueUnit.BasisPoints,
          tickSize: 1,
        },
      },
      {
        field: Field.BidZSpreadBPS,
        displayName: refDataDisplayName(Field.BidZSpreadBPS),
        formatValue: refDataFormatValue(Field.BidZSpreadBPS),
        config: {
          type: FieldConfigType.Quantitative,
          buckets: DEFAULT_SPREAD_BPS_BUCKETS,
          valueUnit: ValueUnit.BasisPoints,
          tickSize: 1,
        },
      },
      {
        field: Field.AskZSpreadBPS,
        displayName: refDataDisplayName(Field.AskZSpreadBPS),
        formatValue: refDataFormatValue(Field.AskZSpreadBPS),
        config: {
          type: FieldConfigType.Quantitative,
          buckets: DEFAULT_SPREAD_BPS_BUCKETS,
          valueUnit: ValueUnit.BasisPoints,
          tickSize: 1,
        },
      },
    ],
  } as ConventionalRiskFactorConfig,

  // OAS
  oas: {
    configKey: ConfigKey.OAS,
    displayName: 'OAS',
    type: 'conventional',
    fields: [
      {
        field: Field.MidOAS,
        displayName: refDataDisplayName(Field.MidOAS),
        formatValue: refDataFormatValue(Field.MidOAS),
        config: {
          type: FieldConfigType.Quantitative,
          buckets: DEFAULT_SPREAD_BPS_BUCKETS,
          valueUnit: ValueUnit.BasisPoints,
          tickSize: 1,
        },
      },
      {
        field: Field.BidOAS,
        displayName: refDataDisplayName(Field.BidOAS),
        formatValue: refDataFormatValue(Field.BidOAS),
        config: {
          type: FieldConfigType.Quantitative,
          buckets: DEFAULT_SPREAD_BPS_BUCKETS,
          valueUnit: ValueUnit.BasisPoints,
          tickSize: 1,
        },
      },
      {
        field: Field.AskOAS,
        displayName: refDataDisplayName(Field.AskOAS),
        formatValue: refDataFormatValue(Field.AskOAS),
        config: {
          type: FieldConfigType.Quantitative,
          buckets: DEFAULT_SPREAD_BPS_BUCKETS,
          valueUnit: ValueUnit.BasisPoints,
          tickSize: 1,
        },
      },
    ],
  } as ConventionalRiskFactorConfig,

  // S&P outlook
  sp_outlook: {
    configKey: ConfigKey.SpOutlook,
    displayName: refDataDisplayName(Field.SpOutlook),
    type: 'conventional',
    fields: [
      {
        field: Field.SpOutlook,
        displayName: refDataDisplayName(Field.SpOutlook),
        formatValue: refDataFormatValue(Field.SpOutlook),
        config: {
          type: FieldConfigType.Categorical,
          entityLabel: { singular: 'outlook', plural: 'outlooks' },
        },
      },
    ],
    relevantAssetClasses: ALL_ASSET_CLASSES,
  } as ConventionalRiskFactorConfig,

  // S&P creditwatch
  sp_creditwatch: {
    configKey: ConfigKey.SpCreditwatch,
    displayName: refDataDisplayName(Field.SpCreditwatch),
    type: 'conventional',
    fields: [
      {
        field: Field.SpCreditwatch,
        displayName: refDataDisplayName(Field.SpCreditwatch),
        formatValue: refDataFormatValue(Field.SpCreditwatch),
        config: {
          type: FieldConfigType.Categorical,
          entityLabel: { singular: 'creditwatch', plural: 'creditwatches' },
        },
      },
    ],
    relevantAssetClasses: ALL_ASSET_CLASSES,
  } as ConventionalRiskFactorConfig,

  // Sukuk contract type
  sukuk_contract_type: {
    configKey: ConfigKey.SukukContractType,
    displayName: refDataDisplayName(Field.SukukContractType),
    type: 'conventional',
    fields: [
      {
        field: Field.SukukContractType,
        displayName: refDataDisplayName(Field.SukukContractType),
        formatValue: refDataFormatValue(Field.SukukContractType),
        config: {
          type: FieldConfigType.Categorical,
          entityLabel: {
            singular: 'sukuk contract type',
            plural: 'sukuk contract types',
          },
        },
      },
    ],
    relevantAssetClasses: ['corporate', 'government'],
  } as ConventionalRiskFactorConfig,

  // Sukuk type
  sukuk_type: {
    configKey: ConfigKey.SukukType,
    displayName: refDataDisplayName(Field.SukukType),
    type: 'conventional',
    fields: [
      {
        field: Field.SukukType,
        displayName: refDataDisplayName(Field.SukukType),
        formatValue: refDataFormatValue(Field.SukukType),
        config: {
          type: FieldConfigType.Categorical,
          entityLabel: {
            singular: 'sukuk type',
            plural: 'sukuk types',
          },
        },
      },
    ],
    relevantAssetClasses: ['corporate', 'government'],
  } as ConventionalRiskFactorConfig,

  // Insurer
  insurer: {
    configKey: ConfigKey.Insurer,
    displayName: 'Insurer',
    type: 'conventional',
    fields: [
      {
        field: Field.Insurer,
        displayName: 'Insurer',
        formatValue: (value: unknown) =>
          DEFAULT_TRANSFORMS.valueTransformation(value),
        config: {
          type: FieldConfigType.Categorical,
          entityLabel: {
            singular: 'insurer',
            plural: 'insurers',
          },
        },
      },
    ],
    relevantAssetClasses: ['municipal'],
  } as ConventionalRiskFactorConfig,

  // Coco event type
  coco_event_type: {
    configKey: ConfigKey.CocoEventType,
    displayName: 'Trigger event type',
    type: 'conventional',
    fields: [
      {
        field: Field.CocoEventType,
        displayName: 'Trigger event type',
        formatValue: refDataFormatValue(Field.CocoEventType),
        config: {
          type: FieldConfigType.Categorical,
          entityLabel: {
            singular: 'trigger event type',
            plural: 'trigger event types',
          },
        },
      },
    ],
    relevantAssetClasses: ['corporate'],
  } as ConventionalRiskFactorConfig,

  // Coco trigger type
  coco_trigger_type: {
    configKey: ConfigKey.CocoTriggerType,
    displayName: 'Trigger type',
    type: 'conventional',
    fields: [
      {
        field: Field.CocoTriggerType,
        displayName: 'Trigger type',
        formatValue: refDataFormatValue(Field.CocoTriggerType),
        config: {
          type: FieldConfigType.Categorical,
          entityLabel: {
            singular: 'trigger type',
            plural: 'trigger types',
          },
        },
      },
    ],
    relevantAssetClasses: ['corporate'],
  } as ConventionalRiskFactorConfig,

  // Coco trigger action
  coco_trigger_action: {
    configKey: ConfigKey.CocoTriggerAction,
    displayName: 'Trigger action',
    type: 'conventional',
    fields: [
      {
        field: Field.CocoTriggerAction,
        displayName: 'Trigger action',
        formatValue: refDataFormatValue(Field.CocoTriggerAction),
        config: {
          type: FieldConfigType.Categorical,
          entityLabel: {
            singular: 'trigger action',
            plural: 'trigger actions',
          },
        },
      },
    ],
    relevantAssetClasses: ['corporate'],
  } as ConventionalRiskFactorConfig,

  // Coco trigger level
  coco_trigger_level_type: {
    configKey: ConfigKey.CocoTriggerLevelType,
    displayName: 'Trigger level type',
    type: 'conventional',
    fields: [
      {
        field: Field.CocoTriggerLevelType,
        displayName: 'Trigger level type',
        formatValue: refDataFormatValue(Field.CocoTriggerLevelType),
        config: {
          type: FieldConfigType.Categorical,
          entityLabel: {
            singular: 'trigger level',
            plural: 'trigger levels',
          },
        },
      },
    ],
    relevantAssetClasses: ['corporate'],
  } as ConventionalRiskFactorConfig,

  // Moody's outlook
  moodys_outlook: {
    configKey: ConfigKey.MoodysOutlook,
    displayName: refDataDisplayName(Field.MoodysOutlook),
    type: 'conventional',
    fields: [
      {
        field: Field.MoodysOutlook,
        displayName: refDataDisplayName(Field.MoodysOutlook),
        formatValue: refDataFormatValue(Field.MoodysOutlook),
        config: {
          type: FieldConfigType.Categorical,
          entityLabel: {
            singular: 'outlook',
            plural: 'outlooks',
          },
        },
      },
    ],
    relevantAssetClasses: ALL_ASSET_CLASSES,
  } as ConventionalRiskFactorConfig,

  // Floating rate benchmark
  float_benchmark: {
    configKey: ConfigKey.FloatBenchmark,
    displayName: refDataDisplayName(Field.FloatBenchmark),
    type: 'conventional',
    fields: [
      {
        field: Field.FloatBenchmark,
        displayName: refDataDisplayName(Field.FloatBenchmark),
        formatValue: refDataFormatValue(Field.FloatBenchmark),
        config: {
          type: FieldConfigType.Categorical,
          entityLabel: {
            singular: 'benchmark',
            plural: 'benchmarks',
          },
        },
      },
    ],
    relevantAssetClasses: ALL_ASSET_CLASSES,
  } as ConventionalRiskFactorConfig,

  // Make whole benchmark
  make_whole_benchmark: {
    configKey: ConfigKey.MakeWholeBenchmark,
    displayName: refDataDisplayName(Field.MakeWholeBenchmark),
    type: 'conventional',
    fields: [
      {
        field: Field.MakeWholeBenchmark,
        displayName: refDataDisplayName(Field.MakeWholeBenchmark),
        formatValue: refDataFormatValue(Field.MakeWholeBenchmark),
        config: {
          type: FieldConfigType.Categorical,
          entityLabel: {
            singular: 'benchmark',
            plural: 'benchmarks',
          },
        },
      },
    ],
    relevantAssetClasses: ['corporate', 'government', 'municipal', 'agency'],
  } as ConventionalRiskFactorConfig,

  // Make whole spread
  make_whole_spread: {
    configKey: ConfigKey.MakeWholeSpread,
    displayName: refDataDisplayName(Field.MakeWholeSpread),
    type: 'conventional',
    fields: [
      {
        field: Field.MakeWholeSpread,
        displayName: refDataDisplayName(Field.MakeWholeSpread),
        formatValue: refDataFormatValue(Field.MakeWholeSpread),
        config: {
          type: FieldConfigType.Quantitative,
          buckets: DEFAULT_SPREAD_BPS_BUCKETS,
          valueUnit: ValueUnit.BasisPoints,
          tickSize: 1,
        },
      },
    ],
    relevantAssetClasses: [
      'agency',
      'corporate',
      'municipal',
      'cd',
      'government',
    ],
  } as ConventionalRiskFactorConfig,

  // S&P underlying rating
  sp_underlying_rating: {
    configKey: ConfigKey.SpUnderlyingRating,
    displayName: 'S&P underlying rating',
    type: 'conventional',
    fields: [
      {
        field: Field.SpUnderlyingRating,
        displayName: 'S&P underlying rating',
        // biome-ignore lint/suspicious/noExplicitAny: Bulk suppression as part of biome migration
        formatValue: (value: any) =>
          convertNumberToSpRating(value) ||
          referenceDataTransformationConfig.sp_rating.valueTransformation(
            value,
          ),
        config: {
          type: FieldConfigType.Quantitative,
          buckets: DEFAULT_SP_RATING_BUCKETS,
          valueUnit: ValueUnit.Rating,
          tickSize: 1,
        },
      },
    ],
    relevantAssetClasses: ALL_ASSET_CLASSES,
  } as ConventionalRiskFactorConfig,

  // Moody's underlying rating
  moodys_underlying_rating: {
    configKey: ConfigKey.MoodysUnderlyingRating,
    displayName: "Moody's underlying rating",
    type: 'conventional',
    fields: [
      {
        field: Field.MoodysUnderlyingRating,
        displayName: "Moody's underlying rating",
        // biome-ignore lint/suspicious/noExplicitAny: Bulk suppression as part of biome migration
        formatValue: (value: any) =>
          convertNumberToMoodysRating(value) ||
          referenceDataTransformationConfig.moodys_rating.valueTransformation(
            value,
          ),
        config: {
          type: FieldConfigType.Quantitative,
          buckets: DEFAULT_MOODYS_RATING_BUCKETS,
          valueUnit: ValueUnit.Rating,
          tickSize: 1,
        },
      },
    ],
    relevantAssetClasses: ALL_ASSET_CLASSES,
  } as ConventionalRiskFactorConfig,

  // Institutional liquidity score
  liquidity_institutional_aggregate: {
    configKey: ConfigKey.LiquidityInstitutionalAggregate,
    displayName: refDataDisplayName(Field.LiquidityInstitutionalAggregate),
    type: 'conventional',
    fields: [
      {
        field: Field.LiquidityInstitutionalAggregate,
        displayName: refDataDisplayName(Field.LiquidityInstitutionalAggregate),
        formatValue: refDataFormatValue(Field.LiquidityInstitutionalAggregate),
        config: {
          type: FieldConfigType.Quantitative,
          buckets: [1, 2, 3, 4, 5],
          tickSize: 1,
          isDiscrete: true,
        },
      },
    ],
    relevantAssetClasses: ALL_ASSET_CLASSES,
  } as ConventionalRiskFactorConfig,
}

// Exclude-list, so new factors get the scope control by default. Type is
// circular to scope by asset class; CashflowTarget/LadderPolicy have no
// standard header, and LadderPolicyConstraint carries no `scope` on the wire.
const NO_SCOPE_CONTROL_CONFIG_KEYS = new Set<ConfigKey>([
  ConfigKey.Type,
  ConfigKey.CashflowTarget,
  ConfigKey.LadderPolicy,
])

export const SCOPE_CONTROL_CONFIG_KEYS: ConfigKey[] = Object.values(
  RISK_FACTOR_CONFIGS,
)
  .filter((c) => !NO_SCOPE_CONTROL_CONFIG_KEYS.has(c.configKey))
  .map((c) => c.configKey)

const assetClassScope = (classes: AssetClass[]): FilterObject =>
  ({ type: { in: classes } }) as FilterObject

// A scope covering every asset class restricts nothing (see
// intrinsicScopeForConfig), so treat it as no scope rather than stamping a
// redundant `type is-any-of [all classes]` clause that surfaces as a phantom
// Scope row in the rule editor.
const scopeForAssetClasses = (
  classes: AssetClass[],
): FilterObject | undefined =>
  classes.length === 0 || classes.length >= ALL_ASSET_CLASSES.length
    ? undefined
    : assetClassScope(classes)

// The intrinsic scope a risk factor is born with: a factor like Treasury Subtype
// only applies to treasuries, so its scope shows that class rather than all of
// them. Returns {} when the factor is relevant to every asset class (no-op scope).
export function intrinsicScopeForConfig(configKey: ConfigKey): FilterObject {
  const config = RISK_FACTOR_CONFIGS[configKey]
  // Bond features pin asset classes per field, so they have no factor-level scope.
  const relevant =
    config?.type === RiskFactorConfigType.Conventional
      ? (config as ConventionalRiskFactorConfig).relevantAssetClasses
      : undefined
  if (
    !relevant ||
    relevant.length === 0 ||
    relevant.length >= ALL_ASSET_CLASSES.length
  ) {
    return {}
  }
  return assetClassScope(relevant)
}

export const DEFAULT_HIDDEN_RISK_FACTORS = new Set<ConfigKey>([
  // Specially-rendered (own card + dialog), not a generic grid card.
  ConfigKey.LadderPolicy,
  ConfigKey.IceSector1,
  ConfigKey.IceSector4,
  ConfigKey.FilterLists,
  ConfigKey.CountryDomicile,
  ConfigKey.CountryIssue,
  ConfigKey.CountryOfRisk,
  ConfigKey.EmergingStatus,
  ConfigKey.ImpactBondType,
  ConfigKey.CallType,
  ConfigKey.CallFrequency,
  ConfigKey.DTS,
  ConfigKey.SpRating,
  ConfigKey.MoodysRating,
  ConfigKey.IssueAmount,
  ConfigKey.CouponFrequency,
  ConfigKey.GicsSector1,
  ConfigKey.GicsSector2,
  ConfigKey.GicsSector3,
  ConfigKey.GicsSector4,
  ConfigKey.GenericSector,
  ConfigKey.Ticker,
  ConfigKey.MuniSector,
  ConfigKey.BenchmarkSpread,
  ConfigKey.GSpread,
  ConfigKey.ISpread,
  ConfigKey.ZSpread,
  ConfigKey.OAS,
  ConfigKey.SpOutlook,
  ConfigKey.SpCreditwatch,
  ConfigKey.SukukContractType,
  ConfigKey.SukukType,
  ConfigKey.Insurer,
  ConfigKey.CocoEventType,
  ConfigKey.CocoTriggerType,
  ConfigKey.CocoTriggerAction,
  ConfigKey.CocoTriggerLevelType,
  ConfigKey.MoodysOutlook,
  ConfigKey.FloatBenchmark,
  ConfigKey.MakeWholeBenchmark,
  ConfigKey.MakeWholeSpread,
  ConfigKey.SpUnderlyingRating,
  ConfigKey.MoodysUnderlyingRating,
  ConfigKey.LiquidityInstitutionalAggregate,
])

export const retrieveScope = (field: Field): FilterObject | undefined => {
  for (const config of Object.values(RISK_FACTOR_CONFIGS)) {
    // Conventional: scope is at config level
    if (config.type === 'conventional') {
      const { fields, relevantAssetClasses } =
        config as ConventionalRiskFactorConfig
      const hasField = fields.some((f) => f.field === field)
      if (hasField && relevantAssetClasses) {
        return scopeForAssetClasses(relevantAssetClasses)
      }
    }
    // Bond features: scope is at field level
    if (config.type === 'bond_features') {
      const { fields } = config as BondFeaturesRiskFactorConfig
      const fieldConfig = fields.find((f) => f.field === field)
      if (fieldConfig?.relevantAssetClasses) {
        return scopeForAssetClasses(fieldConfig.relevantAssetClasses)
      }
    }
  }

  return undefined
}

// Risk-factor comparison defaults
//
// Each scenario maps to the comparison the user sees on first load (before
// they pick anything themselves). useRiskFactorComparison detects the
// scenario from store availability and substitutes the matching default when
// the stored selection is null. Stored selections persist once the user
// explicitly picks them.

type RiskFactorComparisonScenario =
  | 'newMoneyPreOptimization'
  | 'newMoneyPostOptimization'
  | 'rebalancingPreTrade'
  | 'rebalancingPostTrade'

export const DEFAULT_COMPARISON_BY_SCENARIO: Record<
  RiskFactorComparisonScenario,
  ResolvedRiskFactorComparison
> = {
  newMoneyPreOptimization: {
    primary: 'universe',
    secondary: NONE_SECONDARY,
    mode: 'absolute',
  },
  newMoneyPostOptimization: {
    primary: 'proposed_holdings',
    secondary: NONE_SECONDARY,
    mode: 'absolute',
  },
  rebalancingPreTrade: {
    primary: 'existing_holdings',
    secondary: NONE_SECONDARY,
    mode: 'absolute',
  },
  rebalancingPostTrade: {
    primary: 'proposed_holdings',
    secondary: 'existing_holdings',
    mode: 'absolute',
  },
}

export function riskFactorComparisonScenarioFor({
  hasExisting,
  hasProposed,
}: {
  hasExisting: boolean
  hasProposed: boolean
}): RiskFactorComparisonScenario {
  if (hasExisting && hasProposed) return 'rebalancingPostTrade'
  if (hasExisting) return 'rebalancingPreTrade'
  if (hasProposed) return 'newMoneyPostOptimization'
  return 'newMoneyPreOptimization'
}

// Tax Profile
export enum TaxProfile {
  None = 'none',
  USTaxable = 'us_taxable',
  Custom = 'custom_taxable',
  UK = 'uk_taxable',
}

export const TAX_PROFILE_OPTIONS = [
  { label: 'None', value: TaxProfile.None },
  { label: 'US Taxable', value: TaxProfile.USTaxable },
  { label: 'Custom', value: TaxProfile.Custom },
  { label: 'UK Taxable', value: TaxProfile.UK },
]

export const CURRENCY_OPTIONS: { label: string; value: string }[] = [
  { label: CURRENCY_LABELS[Currency.USD], value: Currency.USD },
  { label: CURRENCY_LABELS[Currency.GBP], value: Currency.GBP },
  { label: CURRENCY_LABELS[Currency.EUR], value: Currency.EUR },
  { label: CURRENCY_LABELS[Currency.CHF], value: Currency.CHF },
  { label: CURRENCY_LABELS[Currency.AUD], value: Currency.AUD },
]

// UI shows bond counts; values are face value (bonds × $1,000 par) to match the optimizer's LCM math.
export const STANDARD_PAR_VALUE = 1000
const MINIMUM_ORDER_BOND_COUNTS = [1, 5, 10, 25, 50, 100, 250, 1000] as const
// Shared by the Min-order-increment field and Min-quantity-per-order dropdown.
export const MINIMUM_ORDER_OPTIONS: {
  label: string
  value: string
}[] = [
  { label: 'None', value: '' },
  ...MINIMUM_ORDER_BOND_COUNTS.map((bonds) => ({
    label: `${bonds.toLocaleString()} ${bonds === 1 ? 'bond' : 'bonds'}`,
    value: String(bonds * STANDARD_PAR_VALUE),
  })),
]

// Investor Information Field Configuration
export enum InvestorInfoField {
  InvestorName = 'investor_name',
  StateOfResidence = 'state_of_residence',
  Currency = 'currency',
  FederalIncomeTax = 'federal_income_tax',
  StateIncomeTax = 'state_income_tax',
  TotalInvestment = 'total_investment',
  // Retained for MinQuantityPerOrderRow export compatibility; no longer rendered in the sidepane.
  MinimumOrderIncrement = 'minimum_order_increment',
  MinimumQuantity = 'minimum_quantity',
  CustomIncomeTax = 'custom_income_tax',
  CustomCapitalGainsTax = 'custom_capital_gains_tax',
}

export interface InvestorFieldConfig {
  field: InvestorInfoField
  label: string
  inputType: HTMLInputTypeAttribute
  placeholder: string
  options?: { label: string; value: string }[]
  disabled?: boolean
}

export const INVESTOR_INFO_CONFIG: Record<
  InvestorInfoField,
  InvestorFieldConfig
> = {
  [InvestorInfoField.InvestorName]: {
    field: InvestorInfoField.InvestorName,
    label: 'Investor',
    inputType: 'text',
    placeholder: 'Enter investor name',
  },
  [InvestorInfoField.StateOfResidence]: {
    field: InvestorInfoField.StateOfResidence,
    label: 'State of residence',
    inputType: 'text',
    placeholder: 'Enter state',
    options: getUSARegionOptions,
  },
  [InvestorInfoField.Currency]: {
    field: InvestorInfoField.Currency,
    label: 'Currency',
    inputType: 'text',
    placeholder: 'USD',
    disabled: true,
  },
  [InvestorInfoField.FederalIncomeTax]: {
    field: InvestorInfoField.FederalIncomeTax,
    label: 'Federal tax rate',
    inputType: 'percentage',
    placeholder: '0',
  },
  [InvestorInfoField.StateIncomeTax]: {
    field: InvestorInfoField.StateIncomeTax,
    label: 'State tax rate',
    inputType: 'percentage',
    placeholder: '0',
  },
  [InvestorInfoField.TotalInvestment]: {
    field: InvestorInfoField.TotalInvestment,
    label: 'Total cash available',
    inputType: 'currency',
    placeholder: String(DEFAULT_INVESTMENT_AMOUNT),
  },
  [InvestorInfoField.MinimumOrderIncrement]: {
    field: InvestorInfoField.MinimumOrderIncrement,
    label: 'Min order increment',
    inputType: 'text',
    placeholder: 'None',
    options: MINIMUM_ORDER_OPTIONS,
  },
  [InvestorInfoField.MinimumQuantity]: {
    field: InvestorInfoField.MinimumQuantity,
    label: 'Min order quantity',
    inputType: 'currency',
    placeholder: 'None',
  },
  [InvestorInfoField.CustomIncomeTax]: {
    field: InvestorInfoField.CustomIncomeTax,
    label: 'Income tax rate',
    inputType: 'percentage',
    placeholder: '0',
  },
  [InvestorInfoField.CustomCapitalGainsTax]: {
    field: InvestorInfoField.CustomCapitalGainsTax,
    label: 'Capital gains tax rate',
    inputType: 'percentage',
    placeholder: '0',
  },
}

// Tax-related fields shown when "US Taxable" tax profile is selected
export const US_TAXABLE_FIELDS: InvestorInfoField[] = [
  InvestorInfoField.StateOfResidence,
  InvestorInfoField.FederalIncomeTax,
  InvestorInfoField.StateIncomeTax,
]

// Tax-related fields shown when "Custom" tax profile is selected
export const CUSTOM_TAXABLE_FIELDS: InvestorInfoField[] = [
  InvestorInfoField.CustomIncomeTax,
  InvestorInfoField.CustomCapitalGainsTax,
]

// Statistics Banner Config
//
// A stat formats from either one SIDE of the proposal (existing or proposed
// — comparable by construction) or from proposal-level facts that have no
// before side (Trades, Cash — never comparable).

export interface StatFormatOptions {
  currencyCode: string
  ratingsAccess: ReturnType<typeof useGetRatingsAccess>
  // When set, currency stats render in compact notation (e.g. $1.2M). The
  // banner pairs this with a tooltip showing the full, unabbreviated value.
  abbreviate?: boolean
}

interface BaseStatConfig {
  id: StatId
  label: string
  category: 'order' | 'risk_factor'
}

export interface SideStatConfig extends BaseStatConfig {
  kind: 'side'
  formatValue: (side: SleeveStats, opts: StatFormatOptions) => string
  formatSingleValue?: (stats: BannerStats, opts: StatFormatOptions) => string
  // Raw numeric value backing the compact comparison view's direction chevron
  // and delta. Omitted for non-numeric stats (e.g. rating), which render the
  // before→after pair without a delta.
  comparison?: {
    getValue: (side: SleeveStats) => number | null | undefined
    formatDelta: (delta: number, opts: StatFormatOptions) => string
  }
}

interface ProposalStatConfig extends BaseStatConfig {
  kind: 'proposal'
  formatValue: (stats: BannerStats, opts: StatFormatOptions) => string
}

type StatConfig = SideStatConfig | ProposalStatConfig

interface StatCurrencyOptions {
  numDigits?: number
  abbreviate?: boolean
}

function formatCurrencyValue(
  value: number,
  currencyCode: string,
  { numDigits = 0, abbreviate = false }: StatCurrencyOptions = {},
): string {
  if (abbreviate) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(value)
  }
  return formatMoney(value, { numDigits, currency: currencyCode })
}

const formatCount = (value: number) =>
  formatNumber(value, { minimumFractionDigits: 0, maximumFractionDigits: 0 })

export const STAT_CONFIGS: Record<StatId, StatConfig> = {
  // Eligible-universe counts — live-inventory facts, no before side.
  [StatId.EligibleBonds]: {
    id: StatId.EligibleBonds,
    label: 'Eligible bonds',
    category: 'order',
    kind: 'proposal',
    formatValue: (stats) => formatCount(stats.eligibleBondCount),
  },
  [StatId.EligibleIssuers]: {
    id: StatId.EligibleIssuers,
    label: 'Eligible issuers',
    category: 'order',
    kind: 'proposal',
    formatValue: (stats) => formatCount(stats.eligibleIssuerCount),
  },
  [StatId.EligibleUltimateParents]: {
    id: StatId.EligibleUltimateParents,
    label: 'Eligible ultimate parents',
    category: 'order',
    kind: 'proposal',
    formatValue: (stats) => formatCount(stats.eligibleUltimateParentCount),
  },

  // Proposal-level facts — no before side, never comparable.
  [StatId.Cash]: {
    id: StatId.Cash,
    label: 'Cash Remaining',
    category: 'order',
    kind: 'proposal',
    formatValue: (stats, opts) =>
      formatCurrencyValue(stats.sleeveCash, opts.currencyCode, {
        abbreviate: opts.abbreviate,
      }),
  },
  [StatId.NumTrades]: {
    id: StatId.NumTrades,
    label: 'Trades',
    category: 'order',
    kind: 'proposal',
    formatValue: (stats) => formatCount(stats.tradesCount),
  },

  // Side stats — format one side, comparable by construction.
  [StatId.OrderMV]: {
    id: StatId.OrderMV,
    label: 'Order cost',
    category: 'order',
    kind: 'side',
    // Compare pairs show portfolio total MV per snapshot; the single-value
    // tile keeps the optimistic order-allocations sum, which updates
    // instantly on quantity/limit edits while the snapshot waits for the
    // next autosave round-trip.
    formatValue: (side, opts) =>
      formatCurrencyValue(
        side.statistics?.total_market_value ?? 0,
        opts.currencyCode,
        { abbreviate: opts.abbreviate },
      ),
    formatSingleValue: (stats, opts) =>
      formatCurrencyValue(stats.sleeveMarketValue, opts.currencyCode, {
        abbreviate: opts.abbreviate,
      }),
    comparison: {
      getValue: (side) => side.statistics?.total_market_value ?? null,
      formatDelta: (delta, opts) =>
        formatCurrencyValue(delta, opts.currencyCode, {
          abbreviate: opts.abbreviate,
        }),
    },
  },
  // Total portfolio market value per side, from the snapshot statistics.
  [StatId.PortfolioMarketValue]: {
    id: StatId.PortfolioMarketValue,
    label: 'Portfolio MV',
    category: 'order',
    kind: 'side',
    formatValue: (side, opts) =>
      formatCurrencyValue(
        side.statistics?.total_market_value ?? 0,
        opts.currencyCode,
        { abbreviate: opts.abbreviate },
      ),
    comparison: {
      getValue: (side) => side.statistics?.total_market_value ?? null,
      formatDelta: (delta, opts) =>
        formatCurrencyValue(delta, opts.currencyCode, {
          abbreviate: opts.abbreviate,
        }),
    },
  },
  [StatId.NumIssuers]: {
    id: StatId.NumIssuers,
    label: 'Issuers',
    category: 'order',
    kind: 'side',
    formatValue: (side) => formatCount(side.numIssuers),
    comparison: {
      getValue: (side) => side.numIssuers,
      formatDelta: (delta) => formatCount(delta),
    },
  },
  [StatId.NumSecurities]: {
    id: StatId.NumSecurities,
    label: 'Securities',
    category: 'order',
    kind: 'side',
    formatValue: (side) => formatCount(side.numSecurities),
    comparison: {
      getValue: (side) => side.numSecurities,
      formatDelta: (delta) => formatCount(delta),
    },
  },
  [StatId.AvgLimitYTW]: {
    id: StatId.AvgLimitYTW,
    label: 'Limit YTW',
    category: 'order',
    kind: 'side',
    formatValue: (side) =>
      formatYield(
        side.statistics?.average_purchase_ytw,
        true,
        PROPOSAL_FORMATTING.STAT_DECIMALS,
      ),
    comparison: {
      getValue: (side) => side.statistics?.average_purchase_ytw ?? null,
      formatDelta: (delta) =>
        formatYield(delta, true, PROPOSAL_FORMATTING.STAT_DECIMALS),
    },
  },
  [StatId.AvgLimitYTM]: {
    id: StatId.AvgLimitYTM,
    label: 'Limit YTM',
    category: 'order',
    kind: 'side',
    formatValue: (side) =>
      formatYield(
        side.yields.avgLimitYtm,
        true,
        PROPOSAL_FORMATTING.STAT_DECIMALS,
      ),
    comparison: {
      getValue: (side) => side.yields.avgLimitYtm,
      formatDelta: (delta) =>
        formatYield(delta, true, PROPOSAL_FORMATTING.STAT_DECIMALS),
    },
  },
  [StatId.AvgLimitTEY]: {
    id: StatId.AvgLimitTEY,
    label: 'TEY (YTW)',
    category: 'order',
    kind: 'side',
    formatValue: (side) =>
      formatYield(
        side.yields.avgLimitTey,
        true,
        PROPOSAL_FORMATTING.STAT_DECIMALS,
      ),
    comparison: {
      getValue: (side) => side.yields.avgLimitTey,
      formatDelta: (delta) =>
        formatYield(delta, true, PROPOSAL_FORMATTING.STAT_DECIMALS),
    },
  },
  [StatId.AvgPostTaxLimitYTW]: {
    id: StatId.AvgPostTaxLimitYTW,
    label: 'Post-tax YTW',
    category: 'order',
    kind: 'side',
    formatValue: (side) =>
      formatYield(
        side.statistics?.average_market_post_tax_ytw,
        true,
        PROPOSAL_FORMATTING.STAT_DECIMALS,
      ),
    comparison: {
      getValue: (side) => side.statistics?.average_market_post_tax_ytw ?? null,
      formatDelta: (delta) =>
        formatYield(delta, true, PROPOSAL_FORMATTING.STAT_DECIMALS),
    },
  },
  [StatId.AvgWeightPerBond]: {
    id: StatId.AvgWeightPerBond,
    label: 'Wt/Bond',
    category: 'order',
    kind: 'side',
    formatValue: (side) => {
      const wt = side.yields.avgWeightPerBond
      if (wt == null) return PLACEHOLDER
      return formatPercentage(wt * 100, {
        numDigits: PROPOSAL_FORMATTING.STAT_DECIMALS,
      })
    },
    comparison: {
      getValue: (side) => side.yields.avgWeightPerBond,
      formatDelta: (delta) =>
        formatPercentage(delta * 100, {
          numDigits: PROPOSAL_FORMATTING.STAT_DECIMALS,
        }),
    },
  },
  [StatId.AvgEvalYTW]: {
    id: StatId.AvgEvalYTW,
    label: 'Eval mid YTW',
    category: 'risk_factor',
    kind: 'side',
    formatValue: (side) =>
      formatYield(
        side.statistics?.average_ytw,
        true,
        PROPOSAL_FORMATTING.STAT_DECIMALS,
      ),
    comparison: {
      getValue: (side) => side.statistics?.average_ytw ?? null,
      formatDelta: (delta) =>
        formatYield(delta, true, PROPOSAL_FORMATTING.STAT_DECIMALS),
    },
  },
  [StatId.AvgEvalYTM]: {
    id: StatId.AvgEvalYTM,
    label: 'Eval mid YTM',
    category: 'risk_factor',
    kind: 'side',
    formatValue: (side) =>
      formatYield(
        side.yields.avgEvalYtm,
        true,
        PROPOSAL_FORMATTING.STAT_DECIMALS,
      ),
    comparison: {
      getValue: (side) => side.yields.avgEvalYtm,
      formatDelta: (delta) =>
        formatYield(delta, true, PROPOSAL_FORMATTING.STAT_DECIMALS),
    },
  },
  [StatId.AvgEvalTEY]: {
    id: StatId.AvgEvalTEY,
    label: 'Eval mid TEY',
    category: 'risk_factor',
    kind: 'side',
    formatValue: (side) =>
      formatYield(
        side.yields.avgEvalTey,
        true,
        PROPOSAL_FORMATTING.STAT_DECIMALS,
      ),
    comparison: {
      getValue: (side) => side.yields.avgEvalTey,
      formatDelta: (delta) =>
        formatYield(delta, true, PROPOSAL_FORMATTING.STAT_DECIMALS),
    },
  },
  [StatId.AvgRating]: {
    id: StatId.AvgRating,
    label: 'Avg Lowest Rating',
    category: 'risk_factor',
    kind: 'side',
    formatValue: (side, opts) => {
      const { hasSPAccess, isLoading } = opts.ratingsAccess
      const lowestRating = side.statistics?.average_lowest_rating
      // average_lowest_rating is in S&P format, so only show it to users with S&P access.
      // Fall back to getRatingDisplay (which gates on access) when missing or unauthorized.
      if (hasSPAccess && !isLoading && lowestRating) {
        return lowestRating
      }
      return getRatingDisplay(
        opts.ratingsAccess,
        side.statistics?.average_moodys_rating,
        side.statistics?.average_sp_rating,
      ).value
    },
  },
  [StatId.AvgDuration]: {
    id: StatId.AvgDuration,
    label: 'Duration',
    category: 'risk_factor',
    kind: 'side',
    formatValue: (side) => {
      const value = side.statistics?.average_duration
      if (value === undefined) return PLACEHOLDER
      return formatNumber(value, {
        minimumFractionDigits: PROPOSAL_FORMATTING.STAT_DECIMALS,
        maximumFractionDigits: PROPOSAL_FORMATTING.STAT_DECIMALS,
      })
    },
    comparison: {
      getValue: (side) => side.statistics?.average_duration ?? null,
      formatDelta: (delta) =>
        formatNumber(delta, {
          minimumFractionDigits: PROPOSAL_FORMATTING.STAT_DECIMALS,
          maximumFractionDigits: PROPOSAL_FORMATTING.STAT_DECIMALS,
        }),
    },
  },
  [StatId.AvgMaturity]: {
    id: StatId.AvgMaturity,
    label: 'Maturity',
    category: 'risk_factor',
    kind: 'side',
    formatValue: (side) => formatYears(side.statistics?.average_maturity),
    comparison: {
      getValue: (side) => side.statistics?.average_maturity ?? null,
      formatDelta: (delta) => formatYears(delta),
    },
  },
  [StatId.AvgCoupon]: {
    id: StatId.AvgCoupon,
    label: 'Coupon',
    category: 'risk_factor',
    kind: 'side',
    formatValue: (side) =>
      formatYield(
        side.statistics?.average_coupon,
        true,
        PROPOSAL_FORMATTING.STAT_DECIMALS,
      ),
    comparison: {
      getValue: (side) => side.statistics?.average_coupon ?? null,
      formatDelta: (delta) =>
        formatYield(delta, true, PROPOSAL_FORMATTING.STAT_DECIMALS),
    },
  },
  [StatId.AvgZSpread]: {
    id: StatId.AvgZSpread,
    label: 'Z-Spread',
    category: 'risk_factor',
    kind: 'side',
    formatValue: (side) => formatBps(side.statistics?.average_z_spread),
    comparison: {
      getValue: (side) => side.statistics?.average_z_spread ?? null,
      formatDelta: (delta) => formatBps(delta),
    },
  },
}

export const ALL_STATS: Array<{
  id: StatId
  displayName: string
  visible: boolean
}> = Object.values(STAT_CONFIGS).map((config) => ({
  id: config.id,
  displayName: config.label,
  visible: false,
}))

// Eligible-universe counts. Their values come from live inventory, not a
// proposal snapshot, so version-history cards exclude them.
export const UNIVERSE_COUNT_STAT_IDS: StatId[] = [
  StatId.EligibleBonds,
  StatId.EligibleIssuers,
  StatId.EligibleUltimateParents,
]

export const DEFAULT_STATS: StatId[] = [
  StatId.EligibleBonds,
  StatId.EligibleIssuers,
  StatId.EligibleUltimateParents,
  StatId.PortfolioMarketValue,
  StatId.AvgLimitYTW,
  StatId.AvgRating,
  StatId.AvgDuration,
  StatId.AvgMaturity,
  StatId.OrderMV,
  StatId.Cash,
  StatId.NumTrades,
  StatId.NumSecurities,
]

// Mode Config

export interface ProposalBuilderModeConfig {
  entityName: string
  defaultName: string
  nameEditorAriaLabel: string
  optimizeButtonLabel: string
  rootBreadcrumb: { label: string; href: string }
  showOptimizeButton: boolean
  showSleeveTabs: boolean
  showAddSleeveButton: boolean
  showInvestorInfo: boolean
  showModelInformation: boolean
  showUseTemplateButton: boolean
  showExportButton: boolean
  showShareButton: boolean
  showSubmitTradesButton: boolean
  showPdfDialog: boolean
  // Export menu "Report" item (REPORTS_V2).
  showLaunchReportButton: boolean
  showSleeveNameEditor: boolean
  showModelActionButton: boolean
  showSaveAsModelButton: boolean
  showRiskGroupSelector: boolean
  showResetConstraintsButton: boolean
  expandOverflowActions: boolean
  // Start with the right-hand setup sidepane collapsed (the toggle stays).
  collapseSetupSidepaneByDefault: boolean
  optimizeHotkeyLetter: 'O' | 'P'
  // Risk factors whose dialog header shows the scope control. The control is
  // editable for representable (type/ice_sector_1) scopes and a read-only
  // legible label otherwise, so this governs visibility, not editability.
  scopeControlConfigKeys: ConfigKey[]
  // Risk factors whose max-weight control offers account-size tiering.
  // Empty = feature unavailable in this mode.
  tieredMaxWeightConfigKeys: ConfigKey[]
}

export const MODE_CONFIG: Record<
  ProposalBuilderMode | 'rebalancing',
  ProposalBuilderModeConfig
> = {
  proposal: {
    entityName: 'Proposal',
    defaultName: DEFAULT_PROPOSAL_NAME,
    nameEditorAriaLabel: 'Proposal name',
    optimizeButtonLabel: 'Optimize',
    rootBreadcrumb: { label: 'Proposals', href: '/proposals' },
    showOptimizeButton: true,
    showSleeveTabs: true,
    showAddSleeveButton: true,
    showInvestorInfo: true,
    showModelInformation: false,
    showUseTemplateButton: true,
    showExportButton: true,
    showShareButton: true,
    showSubmitTradesButton: true,
    showSleeveNameEditor: true,
    showPdfDialog: true,
    showLaunchReportButton: true,
    showModelActionButton: false,
    showSaveAsModelButton: true,
    showRiskGroupSelector: true,
    showResetConstraintsButton: true,
    expandOverflowActions: false,
    collapseSetupSidepaneByDefault: false,
    optimizeHotkeyLetter: 'O',
    scopeControlConfigKeys: SCOPE_CONTROL_CONFIG_KEYS,
    tieredMaxWeightConfigKeys: [],
  },
  rebalancing: {
    entityName: 'Proposal',
    defaultName: DEFAULT_PROPOSAL_NAME,
    nameEditorAriaLabel: 'Proposal name',
    optimizeButtonLabel: 'Optimize',
    rootBreadcrumb: { label: 'Proposals', href: '/proposals' },
    showOptimizeButton: false,
    showSleeveTabs: true,
    showAddSleeveButton: true,
    showInvestorInfo: true,
    showModelInformation: false,
    showUseTemplateButton: false,
    showExportButton: true,
    showShareButton: true,
    showSubmitTradesButton: false,
    showSleeveNameEditor: true,
    showPdfDialog: true,
    showLaunchReportButton: true,
    showModelActionButton: false,
    showSaveAsModelButton: false,
    showRiskGroupSelector: true,
    showResetConstraintsButton: true,
    expandOverflowActions: true,
    collapseSetupSidepaneByDefault: true,
    optimizeHotkeyLetter: 'O',
    scopeControlConfigKeys: SCOPE_CONTROL_CONFIG_KEYS,
    tieredMaxWeightConfigKeys: [],
  },
  model: {
    entityName: 'Model',
    defaultName: DEFAULT_MODEL_NAME,
    nameEditorAriaLabel: 'Model name',
    optimizeButtonLabel: 'Preview',
    rootBreadcrumb: { label: 'Models', href: '/models' },
    showOptimizeButton: true,
    showSleeveTabs: false,
    showAddSleeveButton: false,
    showInvestorInfo: false,
    showModelInformation: true,
    showUseTemplateButton: true,
    showExportButton: false,
    showShareButton: false,
    showSubmitTradesButton: false,
    showSleeveNameEditor: false,
    showPdfDialog: false,
    showLaunchReportButton: false,
    showModelActionButton: true,
    showSaveAsModelButton: false,
    showRiskGroupSelector: false,
    showResetConstraintsButton: false,
    expandOverflowActions: false,
    collapseSetupSidepaneByDefault: false,
    optimizeHotkeyLetter: 'P',
    scopeControlConfigKeys: SCOPE_CONTROL_CONFIG_KEYS,
    tieredMaxWeightConfigKeys: [ConfigKey.Issuer],
  },
}
